/**
 * MasterDataMonthFormDialog — Create/Edit `market_month`.
 * Compact Sales Contract style.
 */
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import { useEffect, useState } from 'react'

import { useSnackbar } from '@/hooks/useSnackbar'
import {
  useCreateMonthMutation,
  useUpdateMonthMutation,
} from '@/store/api/refsCrudApi'
import type { MarketMonth } from '@/types/api'
import { getErrorMessage } from '@/utils/error'

import {
  compactDialogContentSx,
  dialogTitleSx,
  roSx,
} from '../purchase/dialogStyle'

export interface Props {
  open: boolean
  mode: 'create' | 'edit'
  initialValue?: MarketMonth | null
  onClose: () => void
  onSaved?: () => void
}

type FormState = { market: string; code: string; month: string }

const EMPTY: FormState = { market: '', code: '', month: '' }

export default function MasterDataMonthFormDialog({
  open,
  mode,
  initialValue,
  onClose,
  onSaved,
}: Props) {
  const snack = useSnackbar()
  const isEdit = mode === 'edit'

  const [createMonth, { isLoading: creating }] = useCreateMonthMutation()
  const [updateMonth, { isLoading: updating }] = useUpdateMonthMutation()
  const loading = creating || updating

  const [form, setForm] = useState<FormState>(EMPTY)
  const [touched, setTouched] = useState(false)

  useEffect(() => {
    if (!open) return
    setTouched(false)
    if (isEdit && initialValue) {
      setForm({
        market: initialValue.market ?? '',
        code: initialValue.code ?? '',
        month: initialValue.month ?? '',
      })
    } else {
      setForm(EMPTY)
    }
  }, [open, isEdit, initialValue])

  const set = <K extends keyof FormState>(k: K, v: FormState[K]) =>
    setForm((p) => ({ ...p, [k]: v }))

  // Composite PK (market + code) must be present to create a row.
  const marketEmpty = !form.market.trim()
  const codeEmpty = !form.code.trim()
  const canSave = isEdit || (!marketEmpty && !codeEmpty)

  const handleClose = () => {
    if (loading) return
    onClose()
  }

  const handleSave = async () => {
    if (!isEdit && (marketEmpty || codeEmpty)) {
      setTouched(true)
      return
    }
    if (loading) return
    try {
      if (isEdit && initialValue) {
        await updateMonth({
          market: initialValue.market,
          code: initialValue.code,
          body: { month: form.month.trim() || null },
        }).unwrap()
        snack.success('Month updated')
      } else {
        await createMonth({
          market: form.market.trim(),
          code: form.code.trim(),
          month: form.month.trim() || null,
        } as MarketMonth).unwrap()
        snack.success('Month created')
      }
      onSaved?.()
      onClose()
    } catch (e) {
      snack.error(getErrorMessage(e, isEdit ? 'Failed to update' : 'Failed to create'))
    }
  }

  return (
    <Dialog
      open={open}
      onClose={(_e, reason) => {
        if (loading) return
        if (reason === 'backdropClick') return
        handleClose()
      }}
      maxWidth="sm"
      fullWidth
      scroll="paper"
    >
      <DialogTitle sx={dialogTitleSx}>
        <Typography component="span" sx={{ flex: 1, fontWeight: 700, fontSize: '1rem' }}>
          {isEdit ? 'Edit Month' : 'New Month'}
          {isEdit && initialValue ? (
            <Typography component="span" variant="caption" sx={{ ml: 1, color: 'text.secondary' }}>
              {initialValue.market} / {initialValue.code}
            </Typography>
          ) : null}
        </Typography>
        <Stack direction="row" spacing={0.5}>
          <Button
            type="button"
            variant="contained"
            size="small"
            color="success"
            onClick={() => void handleSave()}
            disabled={!canSave || loading}
          >
            {loading ? <CircularProgress size={14} color="inherit" /> : 'Save'}
          </Button>
          <Button type="button" variant="outlined" size="small" onClick={handleClose} disabled={loading}>
            Cancel
          </Button>
        </Stack>
      </DialogTitle>
      <Divider />
      <DialogContent dividers sx={compactDialogContentSx}>
        {/* Single-column stacked, Access datasheet order (Top / TabIndex): Month, Market, Code */}
        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr', rowGap: 1.5, mt: 0.5 }}>
          <TextField
            label="Month"
            size="small"
            fullWidth
            value={form.month}
            onChange={(e) => set('month', e.target.value)}
            InputLabelProps={{ shrink: true }}
            inputProps={{ tabIndex: 1 }}
          />
          <TextField
            label="Market"
            required
            size="small"
            fullWidth
            value={form.market}
            onChange={(e) => set('market', e.target.value)}
            disabled={isEdit}
            error={!isEdit && touched && marketEmpty}
            InputLabelProps={{ shrink: true }}
            InputProps={isEdit ? { readOnly: true } : undefined}
            inputProps={{ tabIndex: isEdit ? -1 : 2 }}
            sx={isEdit ? roSx : undefined}
            helperText={isEdit ? 'Cannot be changed after creation.' : undefined}
          />
          <TextField
            label="Code"
            required
            size="small"
            fullWidth
            value={form.code}
            onChange={(e) => set('code', e.target.value)}
            disabled={isEdit}
            error={!isEdit && touched && codeEmpty}
            InputLabelProps={{ shrink: true }}
            InputProps={isEdit ? { readOnly: true } : undefined}
            inputProps={{ tabIndex: isEdit ? -1 : 3 }}
            sx={isEdit ? roSx : undefined}
            helperText={isEdit ? 'Cannot be changed after creation.' : undefined}
          />
        </Box>
      </DialogContent>
    </Dialog>
  )
}
